import useStyles from 'isomorphic-style-loader/useStyles';
import { CircleMinus, TriangleAlert } from 'lucide-react';
import type React from 'react';
import type { StrictPopupProps } from 'semantic-ui-react';
import { Popup } from 'semantic-ui-react';

import { EDGE_AWARE_POPPER_MODIFIERS } from '@utils/popup';

import s from './kev-indicator.scss';

interface KEVIndicatorProps {
  /**
   * Whether the vulnerability is on the CISA KEV catalog.
   */
  value: boolean;
  /**
   * When true, render nothing if `value` is false.
   */
  hideFalse?: boolean;
  position?: StrictPopupProps['position'];
}

/**
 * KEVIndicator shows whether a vulnerability is on the CISA Known Exploited
 * Vulnerabilities (KEV) catalog: a red warning triangle when present, a muted
 * circle-minus when absent.
 */
export const KEVIndicator: React.FC<KEVIndicatorProps> = ({
  value,
  hideFalse = false,
  position = 'top center',
}) => {
  useStyles(s);

  if (!value && hideFalse) return null;

  const trigger = value ? (
    <TriangleAlert
      className={s.kevIcon}
      aria-label="Known Exploited Vulnerability"
    />
  ) : (
    <CircleMinus
      className={s.notKevIcon}
      aria-label="Not a Known Exploited Vulnerability"
    />
  );

  return (
    <Popup
      trigger={trigger}
      position={position}
      hoverable
      className={s.popup}
      popperModifiers={EDGE_AWARE_POPPER_MODIFIERS}
    >
      {value ? (
        <>
          <Popup.Header as="h4">Known Exploited Vulnerability</Popup.Header>
          <Popup.Content>
            <p>
              This vulnerability is on the CISA Known Exploited Vulnerabilities
              (KEV) catalog, meaning it is actively being exploited in the wild.
            </p>
            <h4>Action Required</h4>
            <p>
              Apply patches immediately as per vendor instructions.{' '}
              <strong>
                Federal agencies must remediate within CISA-mandated deadlines.
              </strong>
            </p>
          </Popup.Content>
        </>
      ) : (
        <Popup.Content>
          <p>
            This vulnerability is not on the CISA Known Exploited
            Vulnerabilities (KEV) Catalog.
          </p>
        </Popup.Content>
      )}
    </Popup>
  );
};

export default KEVIndicator;
